import React from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  EVIDENCE_TYPES,
  isQualifyingEvidenceType,
  validateUploadFile,
  mapContractAdjustmentError,
} from "./contractAdjustmentsUtils";

// ── Types ────────────────────────────────────────────────────────────
export type AvailableDocument = {
  id: number;
  name: string;
  documentType: string;
  fileUrl?: string | null;
};

export type AttachedEvidence = {
  documentId: number;
  documentName: string;
  evidenceType: string;
};

export interface UseContractAdjustmentEvidenceOptions {
  installationId: number;
  activeAdjustmentId: number | undefined;
  open: boolean;
  isEdit: boolean;
}

export interface UseContractAdjustmentEvidenceReturn {
  availableDocuments: AvailableDocument[];
  attachedEvidence: AttachedEvidence[];
  attachedDocIds: Set<number>;
  isLoadingEvidence: boolean;
  isUploading: boolean;
  isAttaching: boolean;
  isDetaching: boolean;
  isUpdatingType: boolean;
  canSubmitEvidence: boolean;
  attachExisting: (documentId: number, documentName: string) => Promise<void>;
  detachEvidence: (documentId: number) => Promise<void>;
  updateEvidenceType: (documentId: number, evidenceType: string) => Promise<void>;
  uploadAndAttach: (file: File) => Promise<void>;
  readEvidenceFromBackend: () => Promise<AttachedEvidence[]>;
  refetchDocuments: () => void;
}

export function useContractAdjustmentEvidence(
  options: UseContractAdjustmentEvidenceOptions,
): UseContractAdjustmentEvidenceReturn {
  const { installationId, activeAdjustmentId, open, isEdit } = options;

  const [attachedEvidence, setAttachedEvidence] = React.useState<AttachedEvidence[]>([]);
  const [uploadingFile, setUploadingFile] = React.useState(false);

  const documentsQuery = trpc.documents.listByInstallation.useQuery(
    { installationId },
    { enabled: open },
  );

  const evidenceQuery = trpc.contractAdjustments.listEvidence.useQuery(
    { adjustmentId: activeAdjustmentId! },
    { enabled: open && Boolean(activeAdjustmentId) },
  );

  const utils = trpc.useUtils();

  const attachEvidenceMutation = trpc.contractAdjustments.attachEvidence.useMutation();
  const detachEvidenceMutation = trpc.contractAdjustments.detachEvidence.useMutation();
  const updateEvidenceTypeMutation = trpc.contractAdjustments.updateEvidenceType.useMutation();
  const uploadDocMutation = trpc.documents.upload.useMutation();

  // Load evidence when adjustmentId changes
  React.useEffect(() => {
    if (evidenceQuery.data) {
      const evidence = evidenceQuery.data as any[];
      setAttachedEvidence(
        evidence.map((item: any) => ({
          documentId: item.documentId ?? item.id,
          documentName: item.documentName ?? "Documento",
          evidenceType: item.evidenceType ?? "other",
        })),
      );
    }
  }, [evidenceQuery.data]);

  // Reset on open (not edit)
  React.useEffect(() => {
    if (open && !isEdit) {
      setAttachedEvidence([]);
      setUploadingFile(false);
    }
  }, [open, isEdit]);

  // Derived
  const availableDocuments = React.useMemo(() => {
    const docs = (documentsQuery.data || []) as AvailableDocument[];
    return docs.filter(d => d.documentType !== "contract");
  }, [documentsQuery.data]);

  const attachedDocIds = React.useMemo(
    () => new Set(attachedEvidence.map(a => a.documentId)),
    [attachedEvidence],
  );

  const canSubmitEvidence = React.useMemo(() => {
    if (attachedEvidence.length === 0) return false;
    return attachedEvidence.some(d => isQualifyingEvidenceType(d.evidenceType));
  }, [attachedEvidence]);

  // ── Handlers with controlled error handling ─────────────────────────

  const attachExisting = React.useCallback(
    async (documentId: number, documentName: string) => {
      if (!activeAdjustmentId) {
        toast.error("Primero guarda el borrador antes de añadir evidencias.");
        return;
      }
      try {
        await attachEvidenceMutation.mutateAsync({
          adjustmentId: activeAdjustmentId,
          documentId,
          evidenceType: "other",
        });
        setAttachedEvidence(prev => [
          ...prev,
          { documentId, documentName, evidenceType: "other" },
        ]);
        toast.success("Documento vinculado como evidencia");
      } catch (error: any) {
        toast.error(mapContractAdjustmentError(error, "vincular documento"));
      }
    },
    [activeAdjustmentId, attachEvidenceMutation],
  );

  const detachEvidence = React.useCallback(
    async (documentId: number) => {
      if (!activeAdjustmentId) {
        toast.error("No hay borrador activo.");
        return;
      }
      try {
        await detachEvidenceMutation.mutateAsync({
          adjustmentId: activeAdjustmentId,
          documentId,
        });
        setAttachedEvidence(prev => prev.filter(d => d.documentId !== documentId));
        toast.success("Documento desvinculado");
      } catch (error: any) {
        toast.error(mapContractAdjustmentError(error, "desvincular documento"));
      }
    },
    [activeAdjustmentId, detachEvidenceMutation],
  );

  const updateEvidenceType = React.useCallback(
    async (documentId: number, evidenceType: string) => {
      if (!activeAdjustmentId) {
        toast.error("No hay borrador activo.");
        return;
      }
      try {
        // API first, local state only on success
        await updateEvidenceTypeMutation.mutateAsync({
          adjustmentId: activeAdjustmentId,
          documentId,
          evidenceType: evidenceType as any,
        });
        setAttachedEvidence(prev =>
          prev.map(d => (d.documentId === documentId ? { ...d, evidenceType } : d)),
        );
      } catch (error: any) {
        toast.error(mapContractAdjustmentError(error, "cambiar tipo de evidencia"));
      }
    },
    [activeAdjustmentId, updateEvidenceTypeMutation],
  );

  const uploadAndAttach = React.useCallback(
    async (file: File) => {
      if (!file) return;

      // Client-side validation before reading
      const validationError = validateUploadFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      setUploadingFile(true);
      let uploadedDocId: number | undefined;
      try {
        // Phase 1: upload
        const fileData = await readFileAsBase64(file);
        const result = await uploadDocMutation.mutateAsync({
          installationId,
          name: file.name,
          documentType: "other" as any,
          fileData,
          mimeType: file.type || "application/octet-stream",
          description: "Evidencia de modificación contractual",
        });
        uploadedDocId = (result as any)?.documentId ?? (result as any)?.id;
        if (!uploadedDocId) throw new Error("No se recibió ID del documento");
        documentsQuery.refetch();
        toast.success("Documento subido correctamente");

        // Phase 2: attach (best-effort, won't block overall success)
        if (activeAdjustmentId) {
          try {
            await attachEvidenceMutation.mutateAsync({
              adjustmentId: activeAdjustmentId,
              documentId: uploadedDocId,
              evidenceType: "other",
            });
            setAttachedEvidence(prev => [
              ...prev,
              { documentId: uploadedDocId!, documentName: file.name, evidenceType: "other" },
            ]);
          } catch {
            toast.error(
              "El documento se ha subido, pero no se ha podido vincular automáticamente. " +
              "Puedes seleccionarlo manualmente desde la lista de documentos.",
              { duration: 8000 },
            );
          }
        }
      } catch (error: any) {
        toast.error(mapContractAdjustmentError(error, "subir documento"));
      } finally {
        setUploadingFile(false);
      }
    },
    [installationId, activeAdjustmentId, uploadDocMutation, attachEvidenceMutation, documentsQuery],
  );

  const readEvidenceFromBackend = React.useCallback(async (): Promise<AttachedEvidence[]> => {
    if (!activeAdjustmentId) return [];
    const raw = await utils.contractAdjustments.listEvidence.fetch({
      adjustmentId: activeAdjustmentId,
    });
    const list = (raw as any[]) || [];
    return list.map((item: any) => ({
      documentId: item.documentId ?? item.id,
      documentName: item.documentName ?? "Documento",
      evidenceType: item.evidenceType ?? "other",
    }));
  }, [activeAdjustmentId]);

  const refetchDocuments = React.useCallback(() => {
    documentsQuery.refetch();
  }, [documentsQuery]);

  return {
    availableDocuments, attachedEvidence, attachedDocIds,
    isLoadingEvidence: evidenceQuery.isLoading,
    isUploading: uploadingFile,
    isAttaching: attachEvidenceMutation.isPending,
    isDetaching: detachEvidenceMutation.isPending,
    isUpdatingType: updateEvidenceTypeMutation.isPending,
    canSubmitEvidence,
    attachExisting, detachEvidence, updateEvidenceType,
    uploadAndAttach, readEvidenceFromBackend, refetchDocuments,
  };
}

// ── Utility: file → base64 ───────────────────────────────────────────
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = reader.result;
      if (typeof value !== "string") {
        reject(new Error("No se pudo leer el archivo"));
        return;
      }
      resolve(value.split(",")[1] || "");
    };
    reader.onerror = () => reject(reader.error || new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}